// src/pages/dashboard/Marketing.js
// Route: /dashboard/marketing
// Sprint 7.2 — SEO dashboard, subscriber management, reviews snapshot, ConvertKit placeholder

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Megaphone, CheckCircle, Users, Star, ExternalLink,
  Download, Plus, X, ChevronDown, ChevronUp, Send, RefreshCw,
} from 'lucide-react';
import {
  initiateConvertKitConnect, disconnectConvertKit, syncAllToConvertKit,
} from '../../services/convertkit-service';
import { auth, db } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { useScrollToHash } from '../../utils/useScrollToHash';
import { doc, onSnapshot, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import {
  getSubscribers, addSubscriberManually, removeSubscriber, exportSubscribers,
} from '../../services/subscriber-service';
import { subscribeToReviews } from '../../services/review-service';

// ─── Helpers (module scope) ───────────────────────────────────────────────────

function relativeDate(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const diff = Date.now() - date.getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) > 1 ? 's' : ''} ago`;
}

function formatReviewDate(ts) {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts._seconds ? ts._seconds * 1000 : ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function clientDisplayName(review) {
  if (!review.clientName) return 'Anonymous';
  const parts = review.clientName.trim().split(' ');
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
}

// ─── Sub-components (module scope — never re-mounts on parent render) ─────────

function formatNewsletterDate(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function RecentNewsletters({ practitionerId }) {
  const [newsletters, setNewsletters] = useState([]);

  useEffect(() => {
    if (!practitionerId) return;
    const ref = collection(db, 'practitioners', practitionerId, 'newsletters');
    const q = query(ref, orderBy('sentAt', 'desc'), limit(3));
    getDocs(q)
      .then(snap => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setNewsletters(items);
      })
      .catch(() => {});
  }, [practitionerId]);

  return (
    <div className="pt-4 border-t border-gray-100 mt-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Recent Newsletters</h3>
      {newsletters.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No newsletters sent yet.</p>
      ) : (
        <div className="space-y-2.5">
          {newsletters.map(nl => (
            <div key={nl.id} className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{nl.subject}</p>
                <p className="text-xs text-gray-400">
                  Sent to {nl.recipientCount} subscriber{nl.recipientCount !== 1 ? 's' : ''} on {formatNewsletterDate(nl.sentAt)}
                </p>
              </div>
              <div className="flex-shrink-0 mt-0.5">
                <Send className="w-3.5 h-3.5 text-gray-300" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StarRow({ rating, size = 14 }) {
  return (
    <span style={{ display: 'inline-flex', gap: '2px' }}>
      {[1, 2, 3, 4, 5].map(s => (
        <Star
          key={s}
          size={size}
          fill={s <= rating ? '#D4AF5A' : 'none'}
          color={s <= rating ? '#D4AF5A' : '#D1D5DB'}
          strokeWidth={1.5}
        />
      ))}
    </span>
  );
}

function SeoSection({ practitionerData, navigate }) {
  const isPublished = !!(practitionerData.lastPublished || practitionerData.slug);
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const viewsThisMonth = practitionerData.siteViews?.[currentMonthKey] || 0;

  const SEO_FEATURES = [
    { name: 'Google indexing', desc: 'Your site is visible to Google search' },
    { name: 'Social sharing preview', desc: 'When someone shares your link, it shows your name, photo, and description' },
    { name: 'Schema.org structured data', desc: 'Search engines understand you\'re a practitioner offering specific services' },
    { name: 'XML Sitemap', desc: 'Google knows about all your pages and can find them' },
    { name: 'Mobile-friendly design', desc: 'Your site looks great on all devices' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">SEO &amp; Discoverability</h2>
      <p className="text-sm text-gray-500 mb-5">Help clients find you online</p>

      {!isPublished ? (
        <div className="flex flex-col items-center text-center py-6 px-4">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-3">
            <Megaphone className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Publish your DF website to activate these SEO features, or add a booking button to your existing site.
          </p>
          <button
            onClick={() => navigate('/site-editor')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
          >
            Go to Site Editor →
          </button>
        </div>
      ) : (
        <>
          <ul className="space-y-3 mb-5">
            {SEO_FEATURES.map(feature => (
              <li key={feature.name} className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-gray-900 text-sm">{feature.name}</span>
                  <p className="text-gray-500 text-xs mt-0.5">{feature.desc}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-600">
              Site Views This Month:{' '}
              <span className="font-semibold text-gray-900">{viewsThisMonth.toLocaleString()}</span>
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function AddSubscriberModal({ practitionerId, onClose, onSuccess }) {
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { setError('Email is required.'); return; }
    setLoading(true);
    setError('');
    try {
      await addSubscriberManually(practitionerId, {
        email: form.email.trim(),
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to add subscriber.');
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Add Subscriber</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="name@example.com"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={form.firstName}
                onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                placeholder="Optional"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                placeholder="Optional"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-xs">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-60 transition-colors"
            >
              {loading ? 'Adding…' : 'Add Subscriber'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SubscriberRow({ subscriber, onRemoveClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="flex items-center justify-between py-2.5 px-1 rounded-lg transition-colors"
      style={{ background: hovered ? '#F9FAFB' : 'transparent' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div>
        <p className="text-sm font-medium text-gray-900">{subscriber.email}</p>
        <p className="text-xs text-gray-400">{relativeDate(subscriber.subscribedAt)}</p>
      </div>
      {hovered && (
        <button
          onClick={() => onRemoveClick(subscriber)}
          className="text-xs text-gray-400 hover:text-red-500 transition-colors ml-3 flex-shrink-0"
        >
          Remove
        </button>
      )}
    </div>
  );
}

function RemoveConfirmBanner({ subscriber, onConfirm, onCancel, loading }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
      <p className="text-sm text-gray-700 mb-2">
        Remove <span className="font-medium">{subscriber.email}</span> from your list?
      </p>
      <div className="flex gap-2">
        <button
          onClick={onConfirm}
          disabled={loading}
          className="px-3 py-1 bg-red-500 text-white rounded text-xs font-medium hover:bg-red-600 disabled:opacity-60 transition-colors"
        >
          {loading ? 'Removing…' : 'Confirm'}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1 border border-gray-300 text-gray-600 rounded text-xs font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function SubscribersSection({ practitionerId, navigate }) {
  const [subscribers, setSubscribers] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null); // { id, email }
  const [removeLoading, setRemoveLoading] = useState(false);

  useEffect(() => {
    if (!practitionerId) return;
    const unsub = getSubscribers(practitionerId, setSubscribers);
    return () => unsub();
  }, [practitionerId]);

  const handleExportCSV = async () => {
    const data = await exportSubscribers(practitionerId);
    const csv = ['Email,First Name,Last Name,Subscribed At,Source']
      .concat(data.map(s =>
        `${s.email},${s.firstName || ''},${s.lastName || ''},${s.subscribedAt?.toDate?.()?.toLocaleDateString?.() || ''},${s.source}`
      ))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscribers.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRemoveConfirm = async () => {
    if (!removeConfirm) return;
    setRemoveLoading(true);
    try {
      await removeSubscriber(practitionerId, removeConfirm.id);
    } finally {
      setRemoveLoading(false);
      setRemoveConfirm(null);
    }
  };

  const VISIBLE_COUNT = 5;
  const visibleSubscribers = showAll ? subscribers : subscribers.slice(0, VISIBLE_COUNT);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Newsletter &amp; Subscribers</h2>
      <p className="text-sm text-gray-500 mb-5">Grow and manage your email list</p>

      {/* Subscriber count */}
      <div className="mb-5">
        <span className="text-4xl font-bold text-gray-900">{subscribers.length}</span>
        <span className="text-sm text-gray-500 ml-2">subscribers</span>
      </div>

      {/* Subscriber list or empty state */}
      {subscribers.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl py-10 text-center mb-5">
          <Users className="w-8 h-8 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 text-sm mb-1">No subscribers yet</p>
          <p className="text-gray-400 text-xs max-w-xs mx-auto">
            Visitors can subscribe via the newsletter section on your published website.
            You can also add subscribers manually below.
          </p>
        </div>
      ) : (
        <div className="mb-4">
          <div className="divide-y divide-gray-50">
            {visibleSubscribers.map(s => (
              <React.Fragment key={s.id}>
                <SubscriberRow
                  subscriber={s}
                  onRemoveClick={sub => setRemoveConfirm({ id: sub.id, email: sub.email })}
                />
                {removeConfirm?.id === s.id && (
                  <RemoveConfirmBanner
                    subscriber={removeConfirm}
                    onConfirm={handleRemoveConfirm}
                    onCancel={() => setRemoveConfirm(null)}
                    loading={removeLoading}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {subscribers.length > VISIBLE_COUNT && (
            <button
              onClick={() => setShowAll(v => !v)}
              className="mt-2 flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 transition-colors"
            >
              {showAll ? (
                <><ChevronUp className="w-4 h-4" /> Show less</>
              ) : (
                <><ChevronDown className="w-4 h-4" /> View all {subscribers.length} subscribers</>
              )}
            </button>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-purple-300 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Subscriber
        </button>
        <button
          onClick={() => navigate('/dashboard/marketing/compose')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
        >
          Send Newsletter →
        </button>
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Recent newsletters history */}
      <RecentNewsletters practitionerId={practitionerId} />

      {showAddModal && (
        <AddSubscriberModal
          practitionerId={practitionerId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
}

function DisconnectCKModal({ onConfirm, onCancel, loading }) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-gray-900 mb-2">Disconnect ConvertKit?</h3>
        <p className="text-sm text-gray-600 mb-6">
          Your subscribers will remain in ConvertKit, but new signups won't sync automatically.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-60 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-60 transition-colors"
          >
            {loading ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailPlatformSection({ practitionerData, justConnected }) {
  const isConnected = !!(practitionerData?.convertkitConnected);
  const syncCount = practitionerData?.convertkitSyncCount ?? null;
  const lastSyncAt = practitionerData?.convertkitLastSyncAt || null;

  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Trigger initial sync when returning from OAuth
  useEffect(() => {
    if (justConnected && isConnected) {
      setSyncing(true);
      setSyncResult(null);
      syncAllToConvertKit()
        .then(data => setSyncResult(data?.synced ?? 0))
        .catch(() => setSyncResult(null))
        .finally(() => setSyncing(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justConnected]);

  const handleConnect = async () => {
    setConnecting(true);
    setConnectError('');
    try {
      await initiateConvertKitConnect(); // redirects — never returns
    } catch (err) {
      setConnectError(err.message || 'Failed to start ConvertKit connection.');
      setConnecting(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const data = await syncAllToConvertKit();
      setSyncResult(data?.synced ?? 0);
    } catch {
      setSyncResult(null);
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectConvertKit();
      setShowDisconnectModal(false);
    } catch {
      // Firestore listener on the parent will update convertkitConnected
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div id="convertkit-integration" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Email Platform</h2>
      <p className="text-sm text-gray-500 mb-6">
        Advanced email marketing — optional
      </p>

      <div className="flex items-start gap-4">
        {/* CK logo */}
        <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 flex items-center justify-center flex-shrink-0">
          <span className="text-orange-600 font-bold text-sm">CK</span>
        </div>

        <div className="flex-1">
          {/* Name + status */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900">ConvertKit</h3>
            {isConnected && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium border border-green-200">
                <CheckCircle className="w-3 h-3" />
                Connected
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-1">
            Automated welcome emails, scheduled newsletters, and advanced campaigns.
          </p>
          <p className="text-xs text-gray-500 mb-4">
            Free for up to 10,000 subscribers — no credit card required.
          </p>

          {!isConnected ? (
            <>
              {connectError && (
                <p className="text-xs text-red-500 mb-2">{connectError}</p>
              )}
              <button
                onClick={handleConnect}
                disabled={connecting}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-60 transition-colors"
              >
                {connecting ? 'Redirecting…' : 'Connect ConvertKit'}
              </button>
              <p className="text-xs text-gray-400 mt-2">
                Optional. You can send newsletters directly from Divine Frameworks without it.
              </p>
            </>
          ) : (
            <>
              {/* Sync status */}
              <div className="text-sm text-gray-600 mb-3 space-y-1">
                {syncing ? (
                  <p className="flex items-center gap-1.5 text-purple-600">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Syncing subscribers to ConvertKit…
                  </p>
                ) : (
                  <>
                    {syncResult !== null && (
                      <p className="text-green-700 text-xs font-medium">
                        ✓ {syncResult} subscriber{syncResult !== 1 ? 's' : ''} synced
                      </p>
                    )}
                    {syncCount !== null && syncResult === null && (
                      <p>
                        <span className="font-medium text-gray-900">{syncCount}</span>
                        {' '}subscriber{syncCount !== 1 ? 's' : ''} synced to ConvertKit
                      </p>
                    )}
                    {lastSyncAt && syncResult === null && (
                      <p className="text-xs text-gray-400">
                        Last synced: {relativeDate(lastSyncAt)}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleSyncNow}
                  disabled={syncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-60 transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
                  Sync Now
                </button>
                <a
                  href="https://app.kit.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Open ConvertKit
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  onClick={() => setShowDisconnectModal(true)}
                  className="text-sm text-gray-400 hover:text-red-500 transition-colors"
                >
                  Disconnect
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showDisconnectModal && (
        <DisconnectCKModal
          onConfirm={handleDisconnect}
          onCancel={() => setShowDisconnectModal(false)}
          loading={disconnecting}
        />
      )}
    </div>
  );
}

function ReviewsSection({ practitionerId, practitionerData, navigate }) {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (!practitionerId) return;
    const unsub = subscribeToReviews(practitionerId, (all) => {
      setReviews(all.slice(0, 3));
    });
    return () => unsub();
  }, [practitionerId]);

  const publishedReviews = reviews.filter(r => r.status === 'published');
  const reviewStats = practitionerData?.reviewStats;
  const avgRating = reviewStats?.averageRating || 0;
  const totalReviews = reviewStats?.totalReviews || 0;
  const googleConnected = !!(practitionerData?.googleReviews?.placeId || practitionerData?.googleReviews?.connected);
  const googleTotal = practitionerData?.googleReviews?.totalReviews || 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Reviews &amp; Reputation</h2>
      <p className="text-sm text-gray-500 mb-5">See your latest client feedback</p>

      {totalReviews === 0 && publishedReviews.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl py-10 text-center">
          <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center mx-auto mb-3">
            <Star className="w-5 h-5 text-yellow-400" />
          </div>
          <p className="text-gray-500 text-sm mb-1">No reviews yet</p>
          <p className="text-gray-400 text-xs max-w-xs mx-auto">
            Review requests are sent automatically after sessions complete.
          </p>
        </div>
      ) : (
        <>
          {/* Rating summary */}
          {totalReviews > 0 && (
            <div className="flex items-center gap-2 mb-5">
              <span
                className="text-3xl font-bold"
                style={{ color: '#D4AF5A' }}
              >
                ★ {avgRating.toFixed(1)}
              </span>
              <span className="text-sm text-gray-500">based on {totalReviews} review{totalReviews !== 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Review cards */}
          {publishedReviews.length > 0 && (
            <div className="space-y-3 mb-5">
              {publishedReviews.map(r => (
                <div
                  key={r.id}
                  className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900 text-sm">{clientDisplayName(r)}</span>
                    <span className="text-xs text-gray-400">{formatReviewDate(r.submittedAt)}</span>
                  </div>
                  <StarRow rating={r.rating} size={13} />
                  {r.reviewText && (
                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                      {r.reviewText.length > 120 ? `${r.reviewText.slice(0, 120)}…` : r.reviewText}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Google Reviews row */}
      <div className="flex items-center justify-between py-3 border-t border-gray-100 mt-3">
        {googleConnected ? (
          <span className="text-sm text-gray-600">
            Google Reviews: <span className="text-green-600 font-medium">Connected</span>
            {googleTotal > 0 && <span className="text-gray-500"> — {googleTotal} reviews</span>}
          </span>
        ) : (
          <button
            onClick={() => navigate('/dashboard/reviews')}
            className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 transition-colors"
          >
            Import Google Reviews <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={() => navigate('/dashboard/reviews')}
          className="text-sm text-purple-600 hover:text-purple-700 transition-colors font-medium"
        >
          View All Reviews →
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const Marketing = () => {
  useScrollToHash();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [practitionerId, setPractitionerId] = useState(null);
  const [practitionerData, setPractitionerData] = useState(null);

  // Detect ?convertkit=connected return from OAuth — optimistic UI + initial sync trigger
  const justConnected = searchParams.get('convertkit') === 'connected';
  useEffect(() => {
    if (justConnected) {
      // Clean URL without adding to history
      setSearchParams(prev => { prev.delete('convertkit'); return prev; }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auth timing fix: done flag + 3s safety timeout
  useEffect(() => {
    let done = false;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user && !done) {
        done = true;
        setPractitionerId(user.uid);
      }
    });
    const timeout = setTimeout(() => {
      if (!done) {
        done = true;
        // practitionerId stays null if no auth after 3s
      }
    }, 3000);
    return () => { unsub(); clearTimeout(timeout); };
  }, []);

  // Load practitioner doc
  useEffect(() => {
    if (!practitionerId) return;
    const ref = doc(db, 'practitioners', practitionerId);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setPractitionerData(snap.data());
      } else {
        setPractitionerData({});
      }
    });
    return () => unsub();
  }, [practitionerId]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
          <Megaphone className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing</h1>
          <p className="text-sm text-gray-500">Grow your practice and connect with your audience</p>
        </div>
      </div>

      {/* Loading state */}
      {!practitionerData && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      )}

      {practitionerData && (
        <>
          {/* Section 1: SEO */}
          <SeoSection practitionerData={practitionerData} navigate={navigate} />

          {/* Section 2: Subscribers */}
          {practitionerId && (
            <SubscribersSection practitionerId={practitionerId} navigate={navigate} />
          )}

          {/* Section 3: Email Platform */}
          <EmailPlatformSection
            practitionerData={practitionerData}
            justConnected={justConnected}
          />

          {/* Section 4: Reviews */}
          {practitionerId && (
            <ReviewsSection
              practitionerId={practitionerId}
              practitionerData={practitionerData}
              navigate={navigate}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Marketing;
